import React, { useState, useEffect } from 'react';
import { useWalkie } from '../contexts/WalkieContext';

const CHANNEL_TYPES: { value: string; label: string }[] = [
  { value: 'site', label: 'موقع' },
  { value: 'sector', label: 'قطاع' },
  { value: 'department', label: 'قسم' },
  { value: 'emergency', label: 'طوارئ' },
  { value: 'custom', label: 'مخصص' },
];

export default function ChannelManagementPanel() {
  const {
    channels,
    activeChannelId,
    members,
    userRole,
    token,
    createChannel,
    deleteChannel,
    addMemberToChannel,
    removeMemberFromChannel,
    refreshChannels,
  } = useWalkie();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createType, setCreateType] = useState('custom');
  const [createMaxUsers, setCreateMaxUsers] = useState(20);
  const [addMemberEmployeeId, setAddMemberEmployeeId] = useState('');
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelError, setPanelError] = useState('');
  const [panelSuccess, setPanelSuccess] = useState('');

  const activeChannel = channels.find(c => c.id === activeChannelId);
  const canManage = userRole === 'admin' || userRole === 'operations_room' || userRole === 'operations_manager';

  useEffect(() => {
    if (panelSuccess) {
      const t = setTimeout(() => setPanelSuccess(''), 3000);
      return () => clearTimeout(t);
    }
  }, [panelSuccess]);

  if (!canManage) return null;

  // ─── Create Channel ───
  const handleCreate = async () => {
    if (!createName.trim()) return;
    setPanelLoading(true);
    setPanelError('');
    try {
      const res = await fetch('https://secure.hemaya.cloud/api/walkie/channels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: createName.trim(), type: createType, maxUsers: createMaxUsers }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'فشل إنشاء القناة');
      setPanelSuccess('تم إنشاء القناة بنجاح');
      setShowCreateDialog(false);
      setCreateName('');
      refreshChannels();
    } catch (e: any) {
      setPanelError(e.message);
    }
    setPanelLoading(false);
  };

  // ─── Delete Channel ───
  const handleDelete = async () => {
    if (!activeChannelId) return;
    setPanelLoading(true);
    setPanelError('');
    try {
      const res = await fetch(`https://secure.hemaya.cloud/api/walkie/channels/${activeChannelId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'فشل حذف القناة');
      setPanelSuccess('تم حذف القناة');
      setShowDeleteConfirm(false);
      refreshChannels();
    } catch (e: any) {
      setPanelError(e.message);
    }
    setPanelLoading(false);
  };

  // ─── Add Member ───
  const handleAddMember = async () => {
    if (!addMemberEmployeeId.trim() || !activeChannelId) return;
    setPanelLoading(true);
    setPanelError('');
    try {
      const res = await fetch(`https://secure.hemaya.cloud/api/walkie/channels/${activeChannelId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ employeeId: addMemberEmployeeId.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'فشل إضافة العضو');
      setPanelSuccess('تمت إضافة العضو بنجاح');
      setShowAddMemberDialog(false);
      setAddMemberEmployeeId('');
      refreshChannels();
    } catch (e: any) {
      setPanelError(e.message);
    }
    setPanelLoading(false);
  };

  // ─── Remove Member ───
  const handleRemoveMember = async (userId: string) => {
    if (!activeChannelId) return;
    setPanelLoading(true);
    setPanelError('');
    try {
      const res = await fetch(`https://secure.hemaya.cloud/api/walkie/channels/${activeChannelId}/members/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'فشل حذف العضو');
      setPanelSuccess('تم حذف العضو من القناة');
      refreshChannels();
    } catch (e: any) {
      setPanelError(e.message);
    }
    setPanelLoading(false);
  };

  return (
    <div className="bg-gray-800/80 border-b border-gray-700">
      {/* ─── Toolbar ─── */}
      <div className="flex items-center gap-2 px-4 py-2">
        <button
          onClick={() => { setShowCreateDialog(true); setPanelError(''); }}
          className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors flex items-center gap-1"
        >
          ➕ قناة جديدة
        </button>
        {activeChannel && (
          <>
            <button
              onClick={() => { setShowAddMemberDialog(true); setPanelError(''); }}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center gap-1"
            >
              👤 إضافة عضو
            </button>
            <button
              onClick={() => { setShowDeleteConfirm(true); setPanelError(''); }}
              className="px-3 py-1.5 text-sm bg-red-700 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center gap-1"
            >
              🗑️ حذف القناة
            </button>
          </>
        )}
        {panelSuccess && (
          <span className="text-emerald-400 text-sm mr-auto">{panelSuccess}</span>
        )}
        {panelError && (
          <span className="text-red-400 text-sm mr-auto">{panelError}</span>
        )}
      </div>

      {/* ─── Create Channel Dialog ─── */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center" onClick={() => setShowCreateDialog(false)}>
          <div className="bg-gray-800 rounded-xl p-6 w-96 border border-gray-600" onClick={e => e.stopPropagation()} dir="rtl">
            <h3 className="text-lg font-bold text-emerald-400 mb-4">قناة جديدة</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">اسم القناة</label>
                <input type="text" value={createName} onChange={e => setCreateName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-gray-100 focus:border-emerald-500 focus:outline-none"
                  placeholder="مثال: دورية القطاع ب" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">النوع</label>
                <select value={createType} onChange={e => setCreateType(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-gray-100 focus:border-emerald-500 focus:outline-none">
                  {CHANNEL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">الحد الأقصى للمستخدمين</label>
                <input type="number" value={createMaxUsers} onChange={e => setCreateMaxUsers(Number(e.target.value))}
                  min={1} max={200}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-gray-100 focus:border-emerald-500 focus:outline-none" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={handleCreate} disabled={panelLoading || !createName.trim()}
                className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-600 text-white rounded-lg transition-colors">
                {panelLoading ? 'جاري...' : 'إنشاء'}
              </button>
              <button onClick={() => setShowCreateDialog(false)}
                className="flex-1 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors">
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Add Member Dialog ─── */}
      {showAddMemberDialog && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center" onClick={() => setShowAddMemberDialog(false)}>
          <div className="bg-gray-800 rounded-xl p-6 w-96 border border-gray-600" onClick={e => e.stopPropagation()} dir="rtl">
            <h3 className="text-lg font-bold text-blue-400 mb-4">إضافة عضو للقناة</h3>
            <p className="text-sm text-gray-400 mb-3">{activeChannel?.name}</p>
            <div>
              <label className="block text-sm text-gray-400 mb-1">الرقم الوظيفي</label>
              <input type="text" value={addMemberEmployeeId} onChange={e => setAddMemberEmployeeId(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-gray-100 focus:border-blue-500 focus:outline-none"
                placeholder="E.g., 1001" />
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={handleAddMember} disabled={panelLoading || !addMemberEmployeeId.trim()}
                className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 text-white rounded-lg transition-colors">
                {panelLoading ? 'جاري...' : 'إضافة'}
              </button>
              <button onClick={() => setShowAddMemberDialog(false)}
                className="flex-1 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors">
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Confirm Delete Dialog ─── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-gray-800 rounded-xl p-6 w-96 border border-gray-600" onClick={e => e.stopPropagation()} dir="rtl">
            <h3 className="text-lg font-bold text-red-400 mb-4">تأكيد حذف القناة</h3>
            <p className="text-sm text-gray-300">هل أنت متأكد من حذف "{activeChannel?.name}"؟ هذا الإجراء لا يمكن التراجع عنه.</p>
            <div className="flex gap-2 mt-4">
              <button onClick={handleDelete} disabled={panelLoading}
                className="flex-1 py-2 bg-red-700 hover:bg-red-600 disabled:bg-gray-600 text-white rounded-lg transition-colors">
                {panelLoading ? 'جاري...' : 'نعم، احذف'}
              </button>
              <button onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors">
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Member remove buttons in member list ─── */}
      {/* This is handled inline in the member list via the context */}
    </div>
  );
}
